/**
 * @fileoverview Mock backend for the notifications endpoints.
 * @description Registers POST /notifications/device-tokens, GET /notifications and POST
 * /notifications/:id/read on the feature's shared mock adapter (see notifications-mock-adapter.ts),
 * answering with the backend's success/error envelopes. Device token registration always succeeds;
 * the other two read/write the in-memory notifications-mock-store.ts so marking one as read
 * actually sticks for the rest of the session. A no-op when VITE_ENABLE_MOCK_API is false, so switching to a
 * real backend is deleting this file's one import in features/notifications/index.ts.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { notificationsMockAdapter } from './notifications-mock-adapter'
import {
  countUnreadNotifications,
  listNotifications,
  markNotificationRead,
} from './notifications-mock-store'

import type { ApiEnvelope } from '@/shared/types/http.types'

/**
 * @description Wraps a payload in the backend's success envelope.
 * @param {T} data - The payload.
 * @param {string} message - The success message.
 * @returns {ApiEnvelope<T>} The enveloped payload.
 */
function envelope<T>(data: T, message: string): ApiEnvelope<T> {
  return { httpStatus: 200, message, traceId: crypto.randomUUID(), data }
}

if (notificationsMockAdapter) {
  notificationsMockAdapter.onPost('/notifications/device-tokens').reply(200)

  notificationsMockAdapter
    .onGet('/notifications/unread-count')
    .reply(() => [
      200,
      envelope(
        { unreadCount: countUnreadNotifications() },
        'Unread notification count retrieved successfully',
      ),
    ])

  notificationsMockAdapter
    .onGet('/notifications')
    .reply(() => [200, envelope(listNotifications(), 'Notifications retrieved successfully')])

  notificationsMockAdapter.onPost(/\/notifications\/[^/]+\/read$/).reply((config) => {
    const id = config.url?.split('/').at(-2) ?? ''
    const notification = markNotificationRead(id)

    if (!notification) {
      return [
        404,
        {
          httpStatus: 404,
          httpError: 'Not Found',
          appCode: '6001',
          message: `Notification ${id} could not be found`,
          traceId: crypto.randomUUID(),
          data: [],
        },
      ]
    }

    return [
      200,
      envelope(
        {
          notificationDeliveryId: notification.notificationDeliveryId,
          inAppStatus: notification.inAppStatus,
          readAt: notification.readAt,
        },
        'Notification marked as read',
      ),
    ]
  })
}
